import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import { EMPLOYEE_EQUIPMENT_QUERY_KEY } from '@/components/email-templates/constants';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import {
  cancelEquipmentFill,
  closeEquipmentFillNotNeeded,
  resendEquipmentFill,
} from '@/services/equipmentRequests.service';
import { DATE_FORMATS } from '@/utils/formUtils';

const day = (date) => (date ? dayjs(date).format(DATE_FORMATS.DISPLAY_DOTS) : '');
const round = '!rounded-[50px]';

/**
 * HR's request to the head of the sector, on the "Equipment" tile: who it
 * waits for; a head fills it in or says nothing is needed, HR sends it again
 * or cancels it. Once closed, one line about how it ended.
 *
 * @param {{ isHr:boolean, isHead:boolean, open:Object|null, last:Object|null }} props.fill
 * @param {Function} props.onFill - Opens the existing equipment form.
 */
export default function EquipmentFillStrip({ fill, employeeId, onFill }) {
  const { tn } = useEmployeeNoticesT();
  const queryClient = useQueryClient();
  const open = fill?.open;

  const act = useMutation({
    mutationFn: (action) => action(employeeId, open._id),
    onSuccess: (result) => {
      if (result?.notified === false && result?.message) message.warning(result.message);
      else message.success(result?.message);
      queryClient.invalidateQueries({ queryKey: [EMPLOYEE_EQUIPMENT_QUERY_KEY, employeeId] });
    },
    onError: (error) => message.error(errorText(error, tn('equipmentFill.failed'))),
  });

  const confirmProps = {
    okButtonProps: { className: round },
    cancelButtonProps: { className: round },
    cancelText: tn('equipmentFill.confirmBack'),
  };

  if (!open) {
    const last = fill?.last;
    if (!last || !['filled', 'notNeeded'].includes(last.status)) return null;
    return (
      <p className="m-0 text-[12px] text-zinc-500">
        {tn(last.status === 'filled' ? 'equipmentFill.filledInfo' : 'equipmentFill.notNeededInfo', {
          name: last.closedByName || '—',
          date: day(last.closedAt),
        })}
      </p>
    );
  }

  return (
    <section className="grid gap-2 rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-[13px]">
      <h3 className="m-0 text-[11px] font-extrabold uppercase tracking-[0.08em] text-amber-700">
        {tn('equipmentFill.title')}
      </h3>
      {fill.isHead && <p className="m-0 font-semibold text-zinc-900">{tn('equipmentFill.fillPrompt')}</p>}
      <p className="m-0 text-zinc-700">
        {tn('equipmentFill.waiting', {
          heads: open.heads.map((head) => head.name).join(', ') || '—',
          date: day(open.lastSentAt || open.sentAt),
        })}{' '}
        <span className="text-zinc-500">{tn('equipmentFill.sentBy', { name: open.sentByName || '—' })}</span>
      </p>
      {open.note && (
        <p className="m-0 whitespace-pre-line text-zinc-600">
          <span className="font-bold text-zinc-700">{tn('equipmentFill.message')}:</span> {open.note}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {fill.isHead && (
          <>
            <Button type="primary" size="small" className={round} onClick={onFill}>
              {tn('equipmentFill.fill')}
            </Button>
            <Popconfirm
              {...confirmProps}
              title={tn('equipmentFill.notNeededConfirm')}
              okText={tn('equipmentFill.notNeeded')}
              onConfirm={() => act.mutateAsync(closeEquipmentFillNotNeeded)}
            >
              <Button size="small" className={round} loading={act.isPending}>
                {tn('equipmentFill.notNeeded')}
              </Button>
            </Popconfirm>
          </>
        )}
        {fill.isHr && (
          <>
            <Button
              size="small"
              className={round}
              loading={act.isPending}
              onClick={() => act.mutate(resendEquipmentFill)}
            >
              {tn('equipmentFill.resend')}
            </Button>
            <Popconfirm
              {...confirmProps}
              title={tn('equipmentFill.cancelConfirm')}
              okText={tn('equipmentFill.cancel')}
              onConfirm={() => act.mutateAsync(cancelEquipmentFill)}
            >
              <Button size="small" className={round} loading={act.isPending}>
                {tn('equipmentFill.cancel')}
              </Button>
            </Popconfirm>
          </>
        )}
      </div>
    </section>
  );
}
